import {formatDate, formatNumber} from '@angular/common';
import {AbstractControl, FormArray, FormGroup} from '@angular/forms';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {DayTimeOptions} from './classes/day-time-options';
import {TimeOption} from './classes/time-option';
import {ConfirmDialogService} from './services/confirm-dialog-service/confirm-dialog.service';


export class Utils {

    constructor() {

    }

    static isIOS() {
        if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
            return true;
        } else {
            return navigator.maxTouchPoints &&
                navigator.maxTouchPoints > 2 &&
                /MacIntel/.test(navigator.platform);
        }
    }

    static isAndroid() {
        return /Android/i.test(navigator.userAgent);
    }

    static isMobile() {
        return Utils.isIOS() || Utils.isAndroid();
    }

    static getMonday(d: Date) {
        d = new Date(d);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
        return new Date(d.setDate(diff));
    }

    public static setDate(from, to): Date {
        if (!to) {
            to = new Date();
        } else {
            to = new Date(to);
        }
        to.setDate(from.getDate());
        to.setMonth(from.getMonth());
        to.setFullYear(from.getFullYear());
        return to;
    }

    public static formatDate(date: Date | string, format = 'yyyy-MM-dd', locale = 'nl') {
        return formatDate(date, format, locale);
    }

    public static setTimeByString(date: Date, time: string) {
        const splitedTime = time.split(':');
        date.setHours(+splitedTime[0]);
        date.setMinutes(+splitedTime[1]);
        date.setSeconds(0);
        date.setMilliseconds(0);
        return date;
    }

    public static setTime(date: Date, hours: number, minutes: number, seconds = 0) {
        date.setHours(hours);
        date.setMinutes(minutes);
        date.setSeconds(seconds);
        date.setMilliseconds(0);
        return date;
    }

    public static setTimeNewDate(date: Date, hours: number, minutes: number, seconds = 0) {
        const newDate = Utils.newDate(date);
        newDate.setHours(hours);
        newDate.setMinutes(minutes);
        newDate.setSeconds(seconds);
        newDate.setMilliseconds(0);
        return newDate;
    }

    public static minutes(value: number) {
        if (value && typeof value === 'number') {
            const absValue = Math.abs(value);
            let calcVal = absValue;
            let remain = 0;
            if (absValue >= 1440) {
                remain = absValue - (absValue % 1440);
                calcVal = absValue % 1440;
            }
            const date = Utils.setTime(new Date(), 0, 0).setMinutes(calcVal);
            const time = formatDate(date, 'H:mm', 'nl').split(':');
            return `${value < 0 ? '-' : ''}${+time[0] + (remain / 60)}:${time[1]}`;
        }
        return '0:00';
    }

    public static createTimeRange(fromDate, toDate, interval): DayTimeOptions[] {
        const times: Date[] = [];
        const countDate = new Date(fromDate);
        while (countDate.getTime() <= toDate.getTime()) {
            times.push(new Date(countDate));
            countDate.setUTCMinutes(countDate.getUTCMinutes() + interval);
        }
        const dayOptionsList: DayTimeOptions[] = [];
        let dayOptions = new DayTimeOptions();
        times.forEach((time, i) => {
            if (!dayOptions.datetime || dayOptions.datetime.getDay() !== time.getDay() || i === times.length - 1) {
                if (dayOptions.datetime) {
                    dayOptionsList.push(dayOptions);
                }
                dayOptions = new DayTimeOptions();
                dayOptions.datetime = time;
            }
            dayOptions.options.push(new TimeOption(time));
        });
        return dayOptionsList;
    }

    public static newDate(date: Date | number) {
        if (date) {
            return new Date(date);
        }
        return null;
    }

    public static addMinutes(date: Date | number, minutes: number, substract = false) {
        const dateNew = Utils.newDate(date);
        if (substract) {
            dateNew.setUTCMinutes(dateNew.getUTCMinutes() - minutes);
        } else {
            dateNew.setUTCMinutes(dateNew.getUTCMinutes() + minutes);
        }

        return dateNew;
    }

    public static isSubsequent(date: Date) {
        if (date) {
            return date.getUTCMinutes() % 15 !== 0;
        }
        return false;
    }

    public static subsequentOrigDate(date: Date) {
        if (date) {
            date.setUTCMinutes(date.getUTCMinutes() - (date.getUTCMinutes() % 15));
            return date;
        }
        return null;
    }

    public static matchesDate(dateOne: Date, dateTwo: Date) {
        return dateOne && dateTwo && this.formatDate(new Date(dateOne)) === this.formatDate(new Date(dateTwo));
    }

    static isWorkingday(date: Date) {
        return !Utils.isHoliday(date) && date.getDay() !== 0 && date.getDay() !== 6;
    }

    static isHoliday(date: Date) {
        const holidays = [];
        const christmas = new Date(date);
        christmas.setMonth(11);
        christmas.setDate(25);
        holidays.push(this.formatDate(christmas));
        christmas.setDate(26);
        holidays.push(this.formatDate(christmas));

        const easter = this.easter(date.getFullYear());
        holidays.push(this.formatDate(easter)); // first day
        easter.setDate(easter.getDate() + 1);
        holidays.push(this.formatDate(easter)); // second day
        easter.setDate(easter.getDate() - 3);
        holidays.push(this.formatDate(easter)); // g friday

        const ascensionDay = this.easter(date.getFullYear());
        ascensionDay.setDate(ascensionDay.getDate() + 39);
        holidays.push(this.formatDate(ascensionDay));

        const pentecost = this.easter(date.getFullYear());
        pentecost.setDate(pentecost.getDate() + 49);
        holidays.push(this.formatDate(pentecost));
        pentecost.setDate(pentecost.getDate() + 1);
        holidays.push(this.formatDate(pentecost));

        const willyday = new Date(date);
        willyday.setMonth(3);
        willyday.setDate(27);
        holidays.push(this.formatDate(willyday));

        if (date.getFullYear() % 5 === 0) {
            const fday = new Date(date);
            fday.setMonth(4);
            fday.setDate(5);
            holidays.push(this.formatDate(fday));
        }

        const newYear = new Date(date);
        newYear.setMonth(0);
        newYear.setDate(1);
        holidays.push(this.formatDate(newYear));
        newYear.setFullYear(newYear.getFullYear() + 1);
        holidays.push(this.formatDate(newYear));

        return holidays.indexOf(this.formatDate(date)) !== -1;
    }

    private static easter(Y) {
        const C = Math.floor(Y / 100);
        const N = Y - 19 * Math.floor(Y / 19);
        const K = Math.floor((C - 17) / 25);
        let I = C - Math.floor(C / 4) - Math.floor((C - K) / 3) + 19 * N + 15;
        I = I - 30 * Math.floor((I / 30));
        I = I - Math.floor(I / 28) * (1 - Math.floor(I / 28) * Math.floor(29 / (I + 1)) * Math.floor((21 - N) / 11));
        let J = Y + Math.floor(Y / 4) + I + 2 - C + Math.floor(C / 4);
        J = J - 7 * Math.floor(J / 7);
        const L = I - J;
        const M = 3 + Math.floor((L + 40) / 44);
        const D = L + 28 - 31 * Math.floor(M / 4);

        const padout = (number => {
            return (number < 10) ? '0' + number : number;
        });

        return new Date(Y + '-' + padout(M) + '-' + padout(D));
    }

    public static getTimeOrNull(date: Date) {
        if (date !== null && date !== undefined) {
            return (new Date(date)).getTime();
        }
        return null;
    }

    public static getDateOrNull(date: Date) {
        if (date !== null && date !== undefined) {
            return (new Date(date)).getDate();
        }
        return null;
    }

    public static isNight(startDate, endDate) {
        let start = new Date(startDate);
        let end = new Date(endDate);
        return start.getHours() < 6 || start.getHours() >= 20 || end.getHours() >= 20 || end.getHours() < 6;
    }

    public static addTimeOption(newTimeOption: Date, asdf: DayTimeOptions[]) {
        asdf.forEach(a => {
            if (a.datetime.getDate() === newTimeOption.getDate()) {
                a.options.push(new TimeOption(newTimeOption));
            }
        });
        return asdf;
    }

    public static urlTitle(title: string) {
        return title.replace(/ /g, '-').replace(/[^a-zA-Z0-9\-]/g, '');
    }

    public static handleError(error, confirmDialogService: ConfirmDialogService) {
        if (typeof error['message'] !== 'undefined') {
            if (!Array.isArray(error['message'])) {
                error['message'] = [error['message']];
            }
            const errorList = error['message'].reduce((accumulator, message) => {
                return accumulator + `<li>${message}</li>`;
            }, '');
            confirmDialogService.confirm(
                'Fout bij het opslaan',
                `Het opslaan is mislukt om de volgende reden:<ul>${errorList}</ul>`,
                'Oké',
                null
            );
        } else {
            confirmDialogService.confirm(
                'Fout bij het opslaan',
                `Er is een onverwachte fout opgetreden bij het opslaan`,
                'Oké',
                null
            );
        }
    }

    static triggerValidationP(control: AbstractControl, source: AbstractControl[] = [], handleDisabledAsValid = false): Promise<boolean> {
        return new Promise((resolve, reject) => {
            if ((control as FormArray)?.controls?.length === 0) {
                resolve(true);
            }
            const sub = control.statusChanges.pipe(debounceTime(5)).pipe(distinctUntilChanged()).subscribe(status => {
                if (status === 'VALID') {
                    resolve(true);
                    sub.unsubscribe();
                }
                if (status === 'DISABLED') {
                    if (handleDisabledAsValid) {
                        resolve(true);
                    } else {
                        reject();
                    }
                    sub.unsubscribe();
                }
                if (status === 'INVALID') {
                    reject();
                    sub.unsubscribe();
                }
            });
            Utils.triggerValidation(control, source);
        });
    }

    static triggerValidation(control: AbstractControl, source: AbstractControl[] = []) {
        if (control instanceof FormGroup) {
            const group = (control as FormGroup);

            for (const field in group.controls) {
                const c = group.controls[field];

                this.triggerValidation(c, source);
            }
        } else if (control instanceof FormArray) {
            const group = (control as FormArray);

            for (const field in group.controls) {
                const c = group.controls[field];

                this.triggerValidation(c, source);
            }
        } else {
            control.markAllAsTouched();
            control.updateValueAndValidity({emitEvent: source.indexOf(control) === -1});
        }
    }

    static getWeek(date: Date) {
        const handleDate = new Date(date.getTime());
        handleDate.setHours(0, 0, 0, 0);
        // Thursday in current week decides the year.
        handleDate.setDate(handleDate.getDate() + 3 - (handleDate.getDay() + 6) % 7);
        // January 4 is always in week 1.
        const week1 = new Date(handleDate.getFullYear(), 0, 4);
        // Adjust to Thursday in week 1 and count number of weeks from date to week1.
        return 1 + Math.round(((handleDate.getTime() - week1.getTime()) / 86400000
            - 3 + (week1.getDay() + 6) % 7) / 7);
    }

    static dutchDayOfWeek(date: Date) {
        if (!date) {
            return;
        }
        let dayOfWeek = date.getDay();
        if (dayOfWeek == 0) {
            dayOfWeek = 7;
        }
        return dayOfWeek;
    }

    static copyToClipboard(str) {
        function listener(e) {
            e.clipboardData.setData('text/html', str);
            e.clipboardData.setData('text/plain', str);
            e.preventDefault();
        }

        document.addEventListener('copy', listener);
        document.execCommand('copy');
        document.removeEventListener('copy', listener);
    };

    static dateString(date: Date) {
        return Utils.newDate(date)?.toDateString();
    }

    static dateFormatEquals(dateOne: Date | number, dateTwo: Date | number, format: string) {
        return formatDate(Utils.newDate(dateOne), format, 'nl') === formatDate(Utils.newDate(dateTwo), format, 'nl');
    }

    static minuteDuration(dateOne: Date, dateTwo: Date) {
        return (Utils.getTimeOrNull(dateOne) - Utils.getTimeOrNull(dateTwo)) / 1000 / 60;
    }

    static getDateDate(date: Date) {
        if (!date) {
            return date;
        }
        return (new Date(date)).getDate();
    }

    public static isToday(date: Date) {
        const testDate = new Date(date);
        const today = new Date();
        return testDate.getDate() === today.getDate() &&
            testDate.getMonth() === today.getMonth() &&
            testDate.getFullYear() === today.getFullYear();
    }

    public static isPast(date: Date) {
        const today = Utils.getTimeOrNull(new Date());
        const testDate = new Date(date);
        return testDate.getTime() < today;
    }

    /**
     * Returns the earliest date (start of day) that a user is allowed to edit, based on the current time.
     * Before 12:00: 2 working days back. From 12:00: 1 working day back.
     * Working days skip weekends (Sat/Sun).
     */
    public static canEditRegistrationDate(leaveDate: Date, now: Date = new Date(), hasMutationRole: boolean = false): boolean {
        if (leaveDate > now || leaveDate >= Utils.registrationEditAllowedFrom(now)) {
            return true;
        }
        if (hasMutationRole) {
            const prev = Utils.previousIsoPeriod(now);
            const wednesdayAfter = new Date(prev.end);
            wednesdayAfter.setDate(wednesdayAfter.getDate() + 3);
            wednesdayAfter.setHours(23, 59, 59, 999);
            if (leaveDate >= prev.start && leaveDate <= prev.end && now <= wednesdayAfter) {
                return true;
            }
        }
        return false;
    }

    public static registrationEditAllowedFrom(now: Date = new Date()): Date {
        const stepsBack = now.getHours() < 12 ? 2 : 1;
        const date = new Date(now);
        date.setHours(0, 0, 0, 0);
        let steps = 0;
        while (steps < stepsBack) {
            date.setDate(date.getDate() - 1);
            const day = date.getDay(); // 0=Sun, 6=Sat
            if (day !== 0 && day !== 6) {
                steps++;
            }
        }
        return date;
    }

    /**
     * Returns the start and end of the previous 28-day iso period relative to `now`.
     * Periods are counted from the start of the ISO week year (Monday of week 1).
     */
    public static previousIsoPeriod(now: Date = new Date()): { start: Date; end: Date } {
        const DAYS_PER_PERIOD = 28;

        const getYearStart = (year: number): Date => {
            // ISO week 1 Monday of given year
            const jan4 = new Date(year, 0, 4);
            const day = jan4.getDay() || 7; // Mon=1..Sun=7
            const monday = new Date(jan4);
            monday.setDate(jan4.getDate() - day + 1);
            monday.setHours(0, 0, 0, 0);
            return monday;
        };

        const getPeriodStart = (date: Date): Date => {
            let yearStart = getYearStart(date.getFullYear());
            if (date < yearStart) {
                yearStart = getYearStart(date.getFullYear() - 1);
            }
            const daysSince = Math.floor((date.getTime() - yearStart.getTime()) / 86400000);
            const period = Math.floor(daysSince / DAYS_PER_PERIOD);
            const start = new Date(yearStart);
            start.setDate(yearStart.getDate() + period * DAYS_PER_PERIOD);
            return start;
        };

        const currentPeriodStart = getPeriodStart(now);
        // Previous period: go one day before current period start
        const dayBefore = new Date(currentPeriodStart);
        dayBefore.setDate(dayBefore.getDate() - 1);
        const prevStart = getPeriodStart(dayBefore);
        const prevEnd = new Date(currentPeriodStart);
        prevEnd.setDate(prevEnd.getDate() - 1);
        prevEnd.setHours(23, 59, 59, 999);

        return {start: prevStart, end: prevEnd};
    }

    static shortNumber(transformNumber: number, withDecimals = false) {
        if (isNaN(transformNumber)) return null; // will only work value is a number
        if (transformNumber === null) return null;
        if (transformNumber === 0) return 0;
        let abs = Math.abs(transformNumber);
        const rounder = Math.pow(10, 1);
        const isNegative = transformNumber < 0; // will also work for Negative numbers
        let key = '';

        const powers = [
            {key: 'Q', value: Math.pow(10, 15)},
            {key: 'T', value: Math.pow(10, 12)},
            {key: 'B', value: Math.pow(10, 9)},
            {key: 'M', value: Math.pow(10, 6)},
            {key: 'K', value: 1000}
        ];

        for (let i = 0; i < powers.length; i++) {
            let reduced = abs / powers[i].value;
            reduced = Math.round(reduced * rounder) / rounder;
            if (reduced >= 1) {
                abs = reduced;
                key = powers[i].key;
                break;
            }
        }
        return (isNegative ? '-' : '') + formatNumber(abs, 'nl-NL', withDecimals ? '0.0-3' : '0.0') + key;
    }

    public static timeChanged(starttime: Date, endtime: Date): boolean {
        return new Date(starttime).getTime() !== new Date(endtime).getTime();
    }

    public static projectName(project) {
        if (!project) {
            return " other projects";
        }
        let string = project.company_name;
        if (project.parent_parent_name) {
            string += " &raquo; " + project.parent_parent_name;
        }
        if (project.parent_name) {
            string += " &raquo; " + project.parent_name;
        }
        string += " &raquo; " + project.name;

        return string;
    }

    public static sumProps(list, property, dafunction = null) {
        let total = 0;
        if (list && list.length > 0) {
            list.forEach((item) => {
                if (dafunction && typeof dafunction == 'function')
                    total += +dafunction(item, property);
                else
                    total += +item[property];
            })
        }
        return total;
    }

    public static decimalTime(decimal: any) {
        let dec = Math.abs(Math.round((decimal % 1) * 100) / 100 * 60) + "";
        if (parseInt(dec) <= 9) {
            dec = "0" + dec;
        }
        if (decimal > 0) {
            return Math.floor(decimal) + ":" + dec;
        } else {
            if (Math.ceil(decimal) == 0) {
                return '-' + Math.ceil(decimal) + ":" + dec;
            }
            return Math.ceil(decimal) + ":" + dec;
        }

    }
}
